// Per-page configs for the Rockwall County SEO cluster.
// Each export returns the config consumed by <RockwallSEOPage config={...} />.
import {
  CITIES, SHARED_FAQS_LOCAL, ROCKWALL_HUB_PATH,
  TIRZEPATIDE_HUB_PATH, SEMAGLUTIDE_HUB_PATH,
} from "./rockwallSeo";

/* ════════════════════════  ROCKWALL COUNTY HUB  ════════════════════════ */
export const rockwallCountyHubConfig = {
  path: ROCKWALL_HUB_PATH,
  lang: "en",
  langAlt: { en: ROCKWALL_HUB_PATH, es: "/es/rockwall-county-weight-loss" },
  title: "Rockwall County Weight Loss — Tirzepatide, Semaglutide & GLP-1",
  metaDescription:
    "Medical weight loss serving every Rockwall County city — Rockwall, Royse City, Fate, Heath, McLendon-Chisholm, Mobile City. Provider-reviewed Tirzepatide & Semaglutide, shipped to your door. Start at $39 week 1*.",
  eyebrow: "Serving every city in Rockwall County, Texas",
  h1: "Rockwall County Weight Loss — Tirzepatide, Semaglutide & GLP-1",
  heroLead:
    "BestRx is Rockwall County's online medical weight loss authority. Texas-licensed providers, compounded GLP-1 medications, and discreet pharmacy fulfillment — serving Rockwall, Royse City, Fate, Heath, McLendon-Chisholm, and Mobile City. Start your assessment in 7 minutes. New patients begin at $39 the first week*.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Rockwall County Weight Loss" },
  ],
  areaServed: ["Rockwall", "Royse City", "Fate", "Heath", "McLendon-Chisholm", "Mobile City"],
  showTirzVsSema: true,
  sections: [
    {
      id: "why-bestrx",
      h2: "Why Rockwall County residents choose BestRx",
      body: [
        "Rockwall County has grown 70% in the last decade. The healthcare infrastructure hasn't kept up. Most local primary-care offices have weeks-long waits for new-patient appointments, and existing weight-loss clinics typically operate on a med-spa model — no Texas-licensed provider visit, no compounding pharmacy oversight, and no follow-up.",
        "BestRx is built differently. Every patient is reviewed by a Texas-licensed physician or nurse practitioner before any medication is prescribed. Every prescription is fulfilled by a licensed 503A compounding pharmacy. And every patient has direct, ongoing access to their assigned provider — without scheduling a single in-person visit. For Rockwall County residents juggling work, family, kids' sports at Harry Myers Park, and weekend life on Lake Ray Hubbard, that matters.",
        "We've watched residents drive 45 minutes into Dallas, sit in a parking lot for telehealth, and pay $400+ a month for GLP-1 medications they could get at home for a fraction of the cost. BestRx fixes all three: faster intake, lower starting price, and zero commute.",
      ],
      bullets: [
        "Texas-licensed provider review — every patient, every prescription",
        "503A licensed compounding pharmacy fulfillment",
        "$39 first week — full transparency on ongoing pricing",
        "Direct provider messaging through the patient portal",
        "Discreet plain-packaging shipping to your Rockwall County address",
        "No insurance preauthorizations, no copays, no surprises",
      ],
    },
    {
      id: "medications",
      h2: "Weight loss medications available to Rockwall County patients",
      body: [
        "Your BestRx provider can prescribe the full range of medical weight loss medications when clinically appropriate. The two most-requested are compounded Tirzepatide and compounded Semaglutide — the same active ingredients found in Mounjaro/Zepbound and Ozempic/Wegovy, prepared by a licensed compounding pharmacy.",
      ],
      bullets: [
        "Compounded Tirzepatide (5 mg, 7.5 mg, 10 mg, 12.5 mg per 2 mL)",
        "Compounded Semaglutide (titration from 0.25 mg through 2.4 mg weekly)",
        "Oral GLP-1 (compounded oral Semaglutide alternatives)",
        "MIC+B12 lipotropic injections (fat-metabolism support)",
        "L-Carnitine injections (energy + fat-utilization support)",
        "Supportive supplements when appropriate to your program",
      ],
    },
    {
      id: "telehealth-process",
      h2: "Our 4-step telehealth process",
      body: [
        "From your first click to your first injection, BestRx is engineered for speed without cutting corners. Most Rockwall County patients receive their medication within 3–5 business days of completing intake.",
      ],
      bullets: [
        "Step 1 — Take the assessment (≈ 7 minutes). Answer questions about goals, history, medications, and ZIP code.",
        "Step 2 — Provider review (within 24 hours). A Texas-licensed clinician reviews your intake, confirms eligibility, and selects the correct medication and starting dose.",
        "Step 3 — Pharmacy fulfillment. A 503A licensed compounding pharmacy prepares your prescription. Expect cold-pack shipping for injectables.",
        "Step 4 — Delivery + ongoing care. Your medication arrives in plain packaging at your Rockwall County address. Message your provider any time for dose adjustments, side-effect support, or program changes.",
      ],
    },
    {
      id: "serving-all-cities",
      h2: "Serving every city in Rockwall County",
      body: [
        "BestRx is the only medical weight loss provider built specifically for Rockwall County's distributed population. We serve every ZIP code — from the historic downtown of Rockwall to the new construction of Fate, the lakeside estates of Heath, the growth corridor of Royse City, and the rural luxury of McLendon-Chisholm.",
      ],
      bullets: CITIES.map((c) => `${c.name}, TX (${c.population})`),
    },
    {
      id: "outranking-competitors",
      h2: "Why BestRx outperforms local clinics, med spas, and national telehealth",
      body: [
        "Local Rockwall County med spas and weight-loss clinics typically cost $400–$700 per month, require in-person visits, and don't include true ongoing provider oversight. National telehealth brands like Ro, Hims, and WeightWatchers Clinic offer convenience but operate at scale — no Texas-specific provider relationship, generic dosing protocols, and limited compounding flexibility.",
        "BestRx splits the difference: Texas-licensed providers who know Texas patients, transparent self-pay pricing that beats every national competitor we've benchmarked, and the same compounded GLP-1 medications backed by a 503A facility audit trail.",
      ],
    },
  ],
  faqs: [
    {
      q: "Which Rockwall County cities does BestRx serve?",
      a: "Every city — Rockwall, Royse City, Fate, Heath, McLendon-Chisholm, and Mobile City. Because BestRx is 100% telehealth, ZIP code matters only for shipping, not for provider access.",
    },
    {
      q: "How much does medical weight loss cost in Rockwall County through BestRx?",
      a: "New patients start at $39 the first week*. Ongoing monthly pricing depends on medication and dose. We publish transparent pricing during the assessment — no surprises, no contracts.",
    },
    {
      q: "Do you accept insurance for weight loss in Rockwall County?",
      a: "BestRx is a self-pay program. Compounded GLP-1 medications are generally not covered by insurance regardless of provider. Our cash pricing typically beats insurance copay + clinic-visit total cost.",
    },
    {
      q: "Is Tirzepatide or Semaglutide better for Rockwall County patients?",
      a: "It depends on starting BMI, prior GLP-1 experience, and budget. Tirzepatide (dual GIP+GLP-1 agonist) produces 15–22% body-weight reduction on average over 12 months. Semaglutide produces 10–15%. Your BestRx provider will recommend the right medication after reviewing your intake.",
    },
    {
      q: "How fast can I get started if I live in Rockwall, TX?",
      a: "Most Rockwall, Royse City, Fate, Heath, and McLendon-Chisholm patients are reviewed within 24 hours and receive their medication in 3–5 business days.",
    },
    ...SHARED_FAQS_LOCAL("Rockwall County"),
  ],
};

/* ════════════════════════  CITY PAGE FACTORY  ════════════════════════ */
export function buildCityConfig(citySlug) {
  const c = CITIES.find((x) => x.slug === citySlug);
  if (!c) throw new Error("Unknown city: " + citySlug);
  return {
    path: c.path,
    lang: "en",
    langAlt: { en: c.path, es: `/es${c.path}` },
    title: `${c.name} Weight Loss — Tirzepatide & Semaglutide`,
    metaDescription:
      `Medical weight loss for ${c.name}, TX residents. Texas-licensed providers, compounded Tirzepatide & Semaglutide, shipped to your ${c.name} address. Start at $39 week 1*.`,
    eyebrow: `${c.name}, Texas · Rockwall County`,
    h1: `${c.name} Weight Loss — Provider-Reviewed Tirzepatide & Semaglutide`,
    heroLead:
      `BestRx is the medical weight loss authority for ${c.name}, TX (${c.population}). Texas-licensed provider review, compounded GLP-1 medications, and discreet pharmacy fulfillment — entirely online. ${c.intro}`,
    breadcrumbs: [
      { label: "Home", to: "/" },
      { label: "Rockwall County", to: ROCKWALL_HUB_PATH },
      { label: `${c.name} Weight Loss` },
    ],
    areaServed: [c.name],
    showTirzVsSema: true,
    sections: [
      {
        h2: `Why ${c.name} chooses BestRx`,
        body: [
          `${c.lifestyle}`,
          `Most ${c.name} residents searching for "weight loss near me" find one of three options: a Rockwall med-spa, a Dallas in-person clinic, or a national telehealth brand. None are built for the specific lifestyle of ${c.name}. BestRx is the alternative — provider-supervised medical weight loss with the convenience of complete telehealth, designed and priced for Texas residents.`,
          `We are ${c.competitor_angle}. Every prescription comes from a Texas-licensed clinician who reviews your full intake. Every medication ships from a 503A compounding pharmacy. And every patient stays in touch with their provider through our HIPAA-conscious messaging portal — no extra appointment fees, no waiting rooms, no third-party billing.`,
        ],
        bullets: [
          `Serves all ${c.name} ZIP codes`,
          `Same-week provider review for new patients`,
          `Compounded Tirzepatide & Semaglutide`,
          `Direct shipping to your ${c.name} address — plain packaging`,
          `Texas-licensed providers, not generic national telehealth`,
        ],
      },
      {
        h2: `${c.name} neighborhoods we serve`,
        body: [`From historic streets to brand-new construction, BestRx ships to every ${c.name} neighborhood — including:`],
        bullets: c.neighborhoods,
      },
      {
        h2: `Local landmarks our ${c.name} patients know`,
        body: [`Whether you're a longtime resident or new to the area, you'll recognize these landmarks near our most-served ${c.name} delivery addresses:`],
        bullets: c.landmarks,
      },
      {
        h2: `Medications available for ${c.name} patients`,
        body: [
          `Your BestRx provider can prescribe the full medical-weight-loss formulary when clinically appropriate — compounded Tirzepatide, compounded Semaglutide, oral GLP-1 alternatives, and supportive injectables like MIC+B12 and L-Carnitine.`,
        ],
        bullets: [
          "Compounded Tirzepatide (5 / 7.5 / 10 / 12.5 mg per 2 mL)",
          "Compounded Semaglutide (titration from 0.25 to 2.4 mg weekly)",
          "Oral GLP-1 (compounded oral semaglutide formulations)",
          "MIC+B12 lipotropic injections",
          "L-Carnitine support injections",
        ],
      },
      {
        h2: `What to expect — telehealth process for ${c.name}`,
        body: [
          `${c.name} patients complete the entire process online. There is no in-person visit, no drive into Dallas, no parking. Your assessment takes 7 minutes, your provider review happens within 24 hours, and your medication arrives in 3–5 business days.`,
        ],
        bullets: [
          "Step 1 — 7-minute online assessment",
          "Step 2 — Texas-licensed provider review within 24 hrs",
          "Step 3 — Compounding pharmacy fills your prescription",
          "Step 4 — Discreet shipping to your address in " + c.name,
          "Ongoing — message your provider directly for adjustments",
        ],
      },
    ],
    faqs: [
      {
        q: `Where is the nearest BestRx clinic to ${c.name}, TX?`,
        a: `BestRx doesn't operate a brick-and-mortar clinic — and that's the point. We are 100% online for ${c.name} residents. Your Texas-licensed provider review and medication delivery come to you. No drive, no waiting room.`,
      },
      {
        q: `Can my provider in ${c.name} access my BestRx records?`,
        a: `Yes. Upon request we share visit summaries, prescription history, and lab results with any existing primary-care provider in ${c.name} or elsewhere — at no additional cost.`,
      },
      ...SHARED_FAQS_LOCAL(c.name),
    ],
  };
}

/* ════════════════════════  MEDICATION HUBS  ════════════════════════ */
export const tirzepatideHubConfig = {
  path: TIRZEPATIDE_HUB_PATH,
  title: "Tirzepatide in Rockwall County — Provider-Reviewed Compounded GLP-1",
  metaDescription:
    "Compounded Tirzepatide for Rockwall County, Royse City, Fate, Heath & McLendon-Chisholm residents. Provider-reviewed, pharmacy-compounded, shipped to your door. Start at $39 week 1*.",
  eyebrow: "Compounded Tirzepatide · Rockwall County, Texas",
  h1: "Tirzepatide in Rockwall County, TX — Compounded, Provider-Reviewed, Shipped to Your Door",
  heroLead:
    "Tirzepatide is the most-requested medical weight-loss medication in Rockwall County. BestRx provides Texas-licensed provider review and 503A compounding pharmacy fulfillment for residents of Rockwall, Royse City, Fate, Heath, McLendon-Chisholm, and Mobile City. Average 12-month weight reduction in eligible patients: 15–22%.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Rockwall County", to: ROCKWALL_HUB_PATH },
    { label: "Tirzepatide" },
  ],
  areaServed: CITIES.map((c) => c.name),
  showTirzVsSema: true,
  sections: [
    {
      h2: "What is Tirzepatide?",
      body: [
        "Tirzepatide is a once-weekly subcutaneous injectable that activates two metabolic receptors — GIP (glucose-dependent insulinotropic polypeptide) and GLP-1 (glucagon-like peptide-1). The dual action reduces appetite, slows gastric emptying, and improves insulin sensitivity. In clinical trials, eligible patients lost an average of 15–22% of starting body weight over 72 weeks.",
        "The brand-name versions (Mounjaro for type-2 diabetes, Zepbound for chronic weight management) are FDA-approved and manufactured by Eli Lilly. Compounded Tirzepatide is prepared by 503A licensed compounding pharmacies using FDA-grade active pharmaceutical ingredient, and is prescribed when clinically appropriate by your Texas-licensed provider.",
      ],
    },
    {
      h2: "Who qualifies for Tirzepatide in Rockwall County?",
      body: [
        "Your BestRx provider evaluates eligibility based on your full intake — medical history, current medications, weight, BMI, lab work when available, and treatment goals. Tirzepatide is generally considered for adults with a BMI of 30+ (or 27+ with a weight-related comorbidity), without contraindications such as a personal or family history of medullary thyroid carcinoma or Multiple Endocrine Neoplasia syndrome type 2.",
      ],
      bullets: [
        "BMI 30+ (or 27+ with hypertension, sleep apnea, dyslipidemia, etc.)",
        "Age 18–75",
        "No personal or family history of MTC or MEN2",
        "Not pregnant or breastfeeding",
        "Able to self-administer weekly subcutaneous injections",
      ],
    },
    {
      h2: "How Rockwall County patients use Tirzepatide",
      body: [
        "Tirzepatide is administered once weekly via subcutaneous injection into the abdomen, thigh, or upper arm. Your provider will start you at a low dose (typically 2.5 mg) and titrate every 4 weeks based on tolerance and response — up to a maximum of 15 mg weekly. Most Rockwall County patients begin noticing appetite reduction within the first week.",
      ],
      bullets: [
        "Week 1–4: 2.5 mg weekly (initiation dose)",
        "Week 5–8: 5 mg weekly",
        "Week 9–12: 7.5 mg weekly",
        "Week 13+: titrate to 10, 12.5, or 15 mg as directed by your provider",
        "Sharps disposal: BestRx includes a complimentary sharps container with first shipment",
      ],
    },
    {
      h2: "Tirzepatide side effects and what to do",
      body: [
        "The most common side effects are gastrointestinal — nausea, mild constipation, occasional diarrhea, and reduced appetite. These typically peak in the first 1–2 weeks after each dose increase and resolve as your body adapts. Serious side effects are uncommon but include pancreatitis, gallbladder issues, and (rarely) thyroid concerns — all reasons to ensure you're working with a licensed provider who can identify red flags.",
        "BestRx patients have direct messaging access to their assigned provider throughout the program. Most side effects are managed with dose timing changes, hydration, dietary adjustments, or temporary dose reduction — handled in minutes through the portal, not at an in-person visit.",
      ],
    },
    {
      h2: "Tirzepatide pricing — Rockwall County vs national alternatives",
      body: [
        "Brand-name Tirzepatide (Zepbound or Mounjaro through a national pharmacy) typically runs $1,000–$1,300 per month without insurance. Compounded Tirzepatide through BestRx starts dramatically lower — $39 the first week for new patients, with transparent monthly pricing thereafter that beats Ro, Hims, and most local Rockwall County clinics on our latest comparison.",
      ],
    },
  ],
  faqs: [
    {
      q: "Is compounded Tirzepatide legal in Texas?",
      a: "Yes. Compounded Tirzepatide is prepared by 503A licensed compounding pharmacies registered with the Texas State Board of Pharmacy and the FDA. Your BestRx provider must be Texas-licensed to prescribe it for Texas residents.",
    },
    {
      q: "Is compounded Tirzepatide the same as Zepbound or Mounjaro?",
      a: "The active pharmaceutical ingredient is the same Tirzepatide molecule. The difference is the manufacturer (a licensed compounding pharmacy versus Eli Lilly) and price. Compounded versions are prescribed only when clinically appropriate and may include additional ingredients like B12 in some formulations.",
    },
    {
      q: "How fast can I get Tirzepatide shipped to Rockwall County?",
      a: "Most Rockwall County addresses (Rockwall, Royse City, Fate, Heath, McLendon-Chisholm) receive their first Tirzepatide shipment in 3–5 business days following provider approval.",
    },
    {
      q: "Will Tirzepatide work if other GLP-1s didn't?",
      a: "Often yes. Tirzepatide's dual GIP+GLP-1 mechanism produces stronger appetite and weight effects than single-agonist Semaglutide in many patients who plateaued on Ozempic or Wegovy. Your BestRx provider will review your full history before prescribing.",
    },
    ...SHARED_FAQS_LOCAL("Rockwall County"),
  ],
};

export const semaglutideHubConfig = {
  ...tirzepatideHubConfig, // share boilerplate FAQs structure
  path: SEMAGLUTIDE_HUB_PATH,
  title: "Semaglutide in Rockwall County — Compounded GLP-1 Weight Loss",
  metaDescription:
    "Compounded Semaglutide for Rockwall County, Royse City, Fate, Heath & McLendon-Chisholm residents. Provider-reviewed, pharmacy-compounded, shipped to your door. Start at $39 week 1*.",
  eyebrow: "Compounded Semaglutide · Rockwall County, Texas",
  h1: "Semaglutide in Rockwall County, TX — Compounded, Provider-Reviewed, Shipped to Your Door",
  heroLead:
    "Semaglutide is the original blockbuster GLP-1 weight-loss medication. BestRx provides Texas-licensed provider review and 503A compounding pharmacy fulfillment for Rockwall County residents. Average 12-month weight reduction in eligible patients: 10–15%.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Rockwall County", to: ROCKWALL_HUB_PATH },
    { label: "Semaglutide" },
  ],
  sections: [
    {
      h2: "What is Semaglutide?",
      body: [
        "Semaglutide is a once-weekly GLP-1 receptor agonist that reduces appetite, slows gastric emptying, and improves blood-sugar control. Brand-name versions include Ozempic (type-2 diabetes) and Wegovy (chronic weight management), both FDA-approved and manufactured by Novo Nordisk. Compounded Semaglutide is prepared by 503A licensed compounding pharmacies using FDA-grade active pharmaceutical ingredient.",
        "Semaglutide produces an average 10–15% body weight reduction in eligible patients over 68 weeks of treatment — a transformational outcome for a once-weekly injection.",
      ],
    },
    {
      h2: "Who qualifies for Semaglutide in Rockwall County?",
      body: [
        "Eligibility is determined by your BestRx provider after reviewing your intake. The general qualifying criteria match those of other GLP-1 medications.",
      ],
      bullets: [
        "BMI 30+ (or 27+ with comorbidity)",
        "Age 18–75",
        "No personal/family history of MTC or MEN2",
        "Not currently pregnant or breastfeeding",
        "Comfortable with weekly subcutaneous self-injection",
      ],
    },
    {
      h2: "How Rockwall County patients use Semaglutide",
      body: [
        "Semaglutide titration follows a slow ramp to minimize gastrointestinal side effects. Your provider starts you at 0.25 mg weekly and increases every 4 weeks based on tolerance and response, up to a target dose of 2.4 mg weekly.",
      ],
      bullets: [
        "Week 1–4: 0.25 mg weekly",
        "Week 5–8: 0.5 mg weekly",
        "Week 9–12: 1.0 mg weekly",
        "Week 13–16: 1.7 mg weekly",
        "Week 17+: 2.4 mg weekly (target maintenance dose)",
      ],
    },
    {
      h2: "Semaglutide side effects",
      body: [
        "The most common side effects are mild gastrointestinal — nausea, occasional constipation, and reduced appetite. These typically improve as your body adapts. Serious side effects (pancreatitis, gallbladder issues, rare thyroid effects) are uncommon but reinforce the importance of provider oversight — which is why BestRx will not prescribe Semaglutide without a Texas-licensed clinician reviewing your full intake.",
      ],
    },
    {
      h2: "Semaglutide vs Tirzepatide — which is better for Rockwall County patients?",
      body: [
        "Both work. Tirzepatide produces somewhat larger average weight loss (15–22% vs 10–15%) but Semaglutide is the longer-studied option with broader real-world data. Semaglutide tends to be a better starting choice for patients with smaller weight goals, prior GLP-1 sensitivity, or budget considerations. Your BestRx provider will recommend based on your full clinical picture.",
      ],
    },
  ],
  faqs: [
    {
      q: "Is compounded Semaglutide legal in Texas?",
      a: "Yes. 503A licensed compounding pharmacies in Texas are authorized to prepare compounded Semaglutide under prescription from a Texas-licensed provider.",
    },
    {
      q: "Is compounded Semaglutide the same as Ozempic or Wegovy?",
      a: "The active pharmaceutical ingredient (Semaglutide) is the same. The difference is the preparation source — brand-name versions are manufactured by Novo Nordisk; compounded versions are prepared by 503A licensed compounding pharmacies. Pricing and formulation can differ.",
    },
    {
      q: "How fast can I start Semaglutide if I live in Rockwall County?",
      a: "Most Rockwall County patients receive their first Semaglutide shipment within 3–5 business days of completing intake and provider review.",
    },
    {
      q: "Can I switch from Semaglutide to Tirzepatide if I plateau?",
      a: "Yes — many BestRx patients transition between medications. Your provider will manage the transition safely, including washout and re-titration as appropriate.",
    },
    ...SHARED_FAQS_LOCAL("Rockwall County"),
  ],
};
